import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Heart, Clock, MapPin, Settings, Loader2 } from "lucide-react";
import { AppShell } from "../components/AppShell";
import { useProfileMe } from "../hooks/useProfile";

export function Waitlist() {
  const { data: me, isLoading, error } = useProfileMe();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  if (isLoading) {
    return (
      <AppShell hideNav>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
        </div>
      </AppShell>
    );
  }

  if (error || !me) {
    return <Navigate to="/onboarding/fotos" replace />;
  }

  if (!me.onWaitlist) {
    return <Navigate to="/deck" replace />;
  }

  const endsAt = new Date(me.waitlistUntil);
  const totalMs = Math.max(0, endsAt.getTime() - now.getTime());
  const days = Math.floor(totalMs / (86400 * 1000));
  const hours = Math.floor((totalMs % (86400 * 1000)) / (3600 * 1000));
  const mins = Math.floor((totalMs % (3600 * 1000)) / (60 * 1000));
  const secs = Math.floor((totalMs % (60 * 1000)) / 1000);

  return (
    <AppShell hideNav>
      <section className="container max-w-2xl py-14">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-brand-gradient p-10 text-white shadow-glow md:p-14">
          <div className="pointer-events-none absolute -top-16 -right-20 h-72 w-72 rounded-full bg-white/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-72 w-72 rounded-full bg-white/10 blur-3xl" />

          <div className="relative">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-widest backdrop-blur-sm">
              <Clock className="h-3.5 w-3.5" />
              Deine Warteliste
            </span>
            <h1 className="mt-5 text-4xl font-bold leading-tight tracking-tight md:text-5xl">
              Die richtige Liebe braucht Zeit.
            </h1>
            <p className="mt-4 max-w-md text-white/90">
              Hallo {me.displayName}, wir sammeln dein Umfeld — damit du am
              Launch-Tag ein volles Match-Deck bekommst.
            </p>

            <div className="mt-10 grid grid-cols-4 gap-3">
              {[
                { label: "Tage", value: days },
                { label: "Stunden", value: hours },
                { label: "Minuten", value: mins },
                { label: "Sekunden", value: secs },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className="rounded-2xl bg-white/15 p-4 text-center backdrop-blur-md"
                >
                  <div className="text-3xl font-bold tabular-nums md:text-4xl">
                    {value.toString().padStart(2, "0")}
                  </div>
                  <div className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-white/80">
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="card p-6">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-50">
              <MapPin className="h-5 w-5 text-brand-500" />
            </span>
            <h3 className="mt-4 text-lg font-bold tracking-tight">
              Deine Region
            </h3>
            <p className="mt-1 text-sm text-ink-500">
              {me.city || "Deutschlandweit"}
              {me.sightingsToday > 0 && (
                <>
                  {" · "}
                  <span className="font-semibold text-ink-900">
                    {me.sightingsToday}
                  </span>{" "}
                  Sichtungen heute
                </>
              )}
            </p>
          </div>
          <div className="card p-6">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-50">
              <Heart className="h-5 w-5 text-brand-500" fill="currentColor" />
            </span>
            <h3 className="mt-4 text-lg font-bold tracking-tight">Nutze die Zeit</h3>
            <p className="mt-1 text-sm text-ink-500">
              Verfeinere deine Werte oder passe deine Suche an — wir matchen am
              Launch-Tag mit deinem letzten Stand.
            </p>
          </div>
        </div>

        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
          <Link to="/einstellungen" className="btn-secondary">
            <Settings className="h-4 w-4" />
            Profil anpassen
          </Link>
        </div>
      </section>
    </AppShell>
  );
}
